import { router } from "expo-router"
import { View, Pressable } from "react-native"
import { zodResolver } from "@hookform/resolvers/zod"
import { SafeAreaView } from "react-native-safe-area-context"
import { useForm, Controller, Control, useWatch } from "react-hook-form"

import { TransactionFormProps, purchaseTransactionFormSchema } from "@white-labels/input/src/m5azen"
import { z } from "@white-labels/input"

import { KeyboardAwareScrollView } from "@src/components/keyboard-aware-scroll-view"
import { SupplierPicker } from "@src/components/supplier-picker"
import { Button, Text, TextInput } from "@src/components/core"
import { PriceInput } from "../price-input"
import { ar } from "@src/i18n/ar"

import { ProductTransactionForm } from "./product-transaction-form"
import { TransactionSummary } from "./transaction-summary"
import { trpc } from "utils/trpc"

const DEFAULT_STORAGE_PLACE_ID = 1

type TransactionType = "purchase" | "return"

export function TransactionForm({ id }: TransactionFormProps) {
  const utils = trpc.useUtils()
  const isEditMode = id !== undefined

  const transaction = trpc.transactions.get.useQuery({ id: id as number }, { enabled: isEditMode })

  const editTransaction = trpc.transactions.edit.useMutation()
  const createTransaction = trpc.transactions.create.useMutation()

  const isLoading = transaction.isFetching || editTransaction.isPending || createTransaction.isPending

  const { handleSubmit, control, formState } = useForm({
    defaultValues: {
      type: (transaction.data?.type as TransactionType) ?? "purchase",
      supplierId: transaction.data?.supplierId as number,
      storagePlaceId: transaction.data?.storagePlaceId ?? DEFAULT_STORAGE_PLACE_ID,
      discount: transaction.data?.discount,
      products: transaction.data?.products.map((p) => ({
        sku: p.upc as string,
        quantity: p.quantity,
        cost: p.cost,
        suggestedPrice: p.suggestedSalePrice,
      })),
      notes: transaction.data?.notes ?? "",
    },
    resolver: zodResolver(purchaseTransactionFormSchema),
    mode: "onChange",
    criteriaMode: "all",
  })

  const currentType = useWatch({ control, name: "type" }) as TransactionType

  const { errors, isValid } = formState

  const onFormSubmit = handleSubmit(async (data) => {
    if (isEditMode) {
      editTransaction.mutate(
        { ...data, trxId: id },
        {
          onSuccess() {
            utils.transactions.list.invalidate()
            utils.transactions.get.invalidate({ id })
            utils.products.get.invalidate()
            data.products.forEach((p) => {
              utils.products.getByUpc.invalidate({ upc: p.sku })
            })
            router.back()
          },
        },
      )
    } else {
      createTransaction.mutate(data, {
        onSuccess: async () => {
          utils.transactions.list.invalidate()
          utils.products.get.invalidate()
          data.products.forEach((p) => {
            utils.products.getByUpc.invalidate({ upc: p.sku })
          })
          router.back()
        },
      })
    }
  })

  const isSubmitting = createTransaction.isPending || editTransaction.isPending
  const disabled = !isValid || isSubmitting || isLoading
  const loading = isSubmitting || isLoading

  return (
    <KeyboardAwareScrollView className="flex-1 bg-gray-50" bottomOffset={40}>
      <View className="max-w-content-wide mx-auto w-full px-4 md:px-6 lg:px-8 py-6">
        {/* Transaction Type Toggle */}
        <View className="bg-white rounded p-4 mb-4 border border-gray-200">
          <Text className="text-lg font-bold text-gray-800 mb-3">{ar.transactionType}</Text>
          <Controller
            control={control}
            name="type"
            render={({ field: { value, onChange } }) => (
              <View className="flex-row gap-2">
                <Pressable
                  onPress={() => onChange("purchase")}
                  className={`flex-1 py-3 px-4 rounded-lg border ${value === "purchase" ? "bg-green-500 border-green-500" : "bg-white border-gray-300"}`}
                >
                  <Text className={`text-center font-semibold ${value === "purchase" ? "text-white" : "text-gray-700"}`}>{ar.purchaseTransaction}</Text>
                </Pressable>
                <Pressable
                  onPress={() => onChange("return")}
                  className={`flex-1 py-3 px-4 rounded-lg border ${value === "return" ? "bg-orange-500 border-orange-500" : "bg-white border-gray-300"}`}
                >
                  <Text className={`text-center font-semibold ${value === "return" ? "text-white" : "text-gray-700"}`}>{ar.returnTransaction}</Text>
                </Pressable>
              </View>
            )}
          />
        </View>

        <View className="bg-white rounded p-4 mb-4 border border-gray-200">
          <Text className="text-lg font-bold text-gray-800 mb-2">{ar.supplier}</Text>
          <Controller
            control={control}
            name="supplierId"
            render={({ field: { value, onChange } }) => <SupplierPicker value={value ? [value] : []} onSelectionChange={(suppliers) => onChange(suppliers[0] || 0)} />}
          />
          {errors.supplierId && <Text className="text-red-500 text-sm mt-1">{errors.supplierId.message}</Text>}
        </View>
        {/* <View className="bg-white rounded p-4 mb-4 border border-gray-200">
          <Text className="text-lg font-bold text-gray-800 mb-2">{ar.storagePlace}</Text>
          <Controller
            control={control}
            name="storagePlaceId"
            render={({ field: { value, onChange } }) => <StoragePlacePicker value={value} onSelectionChange={onChange} />}
          />
          {errors.storagePlaceId && <Text className="text-red-500 text-sm mt-1">{errors.storagePlaceId.message}</Text>}
        </View> */}

        <View className="bg-white rounded p-4 mb-4 border border-gray-200">
          <ProductTransactionForm control={control as Control<z.infer<typeof purchaseTransactionFormSchema>>} />
        </View>

        <View className="bg-white rounded p-4 mb-4 border border-gray-200">
          <Text className="text-lg font-bold text-gray-800 mb-3">{ar.discount}</Text>
          <View>
            <Controller
              control={control}
              name="discount"
              render={({ field }) => <PriceInput value={field.value ? Number(field.value) : undefined} onValueChange={field.onChange} />}
            />
          </View>
        </View>

        <View className="bg-white rounded p-4 mb-4 border border-gray-200">
          <Text className="text-lg font-bold text-gray-800 mb-3">{ar.notes}</Text>
          <Controller
            control={control}
            name="notes"
            render={({ field }) => {
              return <TextInput value={field.value} placeholder={ar.notes} onChangeText={field.onChange} multiline numberOfLines={3} />
            }}
          />
        </View>

        <TransactionSummary control={control as Control<z.infer<typeof purchaseTransactionFormSchema>>} />

        <Button onPress={onFormSubmit} disabled={disabled} loading={loading}>
          {isEditMode ? ar.saveChanges : currentType === "return" ? ar.createReturn : ar.save}
        </Button>
      </View>

      <SafeAreaView />
    </KeyboardAwareScrollView>
  )
}
